import type { PackageDataSpec } from '@cadstrom/cadstrom-shared';

/**
 * A clause's number, with the version added only when the cited wording has since been replaced. Omits the
 * property; use {@link qualifiedClauseCode} where clauses from several properties appear together.
 *
 * @param clause - The clause version to cite.
 * @returns e.g. `#12`, or `#12 v1` for a replaced wording.
 */
export function clauseCode(
	clause: Pick<PackageDataSpec.PackageDataSpecClause, 'clauseNumber' | 'version' | 'isCurrentVersion'>,
): string {
	return clause.isCurrentVersion ? `#${clause.clauseNumber}` : `#${clause.clauseNumber} v${clause.version}`;
}

/**
 * A clause's number followed by its short name.
 *
 * @param clause - The clause version to label.
 * @returns e.g. `#12 thermal_pads_named_by_net`.
 */
export function clauseLabel(
	clause: Pick<PackageDataSpec.PackageDataSpecClause, 'clauseNumber' | 'version' | 'isCurrentVersion' | 'shortName'>,
): string {
	return `${clauseCode(clause)} ${clause.shortName}`;
}

/**
 * A clause's number preceded by its property, for lists that mix clauses from several properties.
 *
 * @param clause - The clause version to cite.
 * @returns e.g. `pinout #12`, or `pinout #12 v1` for a replaced wording.
 */
export function qualifiedClauseCode(
	clause: Pick<PackageDataSpec.PackageDataSpecClause, 'propertyKey' | 'clauseNumber' | 'version' | 'isCurrentVersion'>,
): string {
	return `${clause.propertyKey} ${clauseCode(clause)}`;
}
